import React, { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import axios from '../../api/axiosConfig';
import PhoneNumberInput from '../../components/input/PhoneNumberInput'; // Assuming this component exists
// import { Link } from 'react-router-dom'; // No longer needed as login link is removed


interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  phoneNumber2: string;
  address: string;
  dateOfBirth: string;
  gender: string;
  membershipStatus: string;
}

const MemberRegistrationForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    phoneNumber2: '',
    address: '',
    dateOfBirth: '',
    gender: '',
    membershipStatus: 'Full-time Member',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false); // New state for modal visibility

  const churchIdentifier = import.meta.env.VITE_APP_CHURCH_IDENTIFIER;

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      phoneNumber2: '',
      address: '',
      dateOfBirth: '',
      gender: '',
      membershipStatus: 'Full-time Member',
    });
    setMessage('');
    setError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError('Please fill in all required fields: First Name, Last Name, and Email.');
      return;
    }

    if (!churchIdentifier) {
      setError("Church identifier is not configured.");
      return;
    }

    try {
      const dataToSend = { ...formData }; // No password fields to destructure anymore
      const response = await axios.post('/members', dataToSend);

      setMessage(response.data.message || 'Member registered successfully!');
      setShowModal(true); // Show the modal on success
      resetForm(); // Clear the form after submission
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'An unexpected error occurred. Please try again.';
      setError(errorMsg);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    // Optionally, you can reset the form or message here if needed,
    // but it's already done in handleSubmit for successful cases.
  };

  return (
    <div className="w-full font-sans">
      <h2 className="text-2xl font-semibold text-center text-gray-800 mb-4">
        Register New Church Member (Data Collection)
      </h2>
      <p className="text-center text-gray-600 mb-8">
        Use this form to add new members to the church record. No account login is created for the member.
      </p>

      {message && (
        <div className="bg-green-50 text-green-800 p-3 rounded-md border border-green-200 mb-4 text-sm">
          {message}
        </div>
      )}
      {error && (
        <div className="bg-red-50 text-red-800 p-3 rounded-md border border-red-200 mb-4 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Primary Phone Number</label>
            <PhoneNumberInput
              value={formData.phoneNumber}
              onChange={(val) => setFormData({ ...formData, phoneNumber: val })}
              error={formData.phoneNumber && formData.phoneNumber.length > 0 && !/^[67]\d{7}$/.test(formData.phoneNumber)
                ? 'Invalid Eswatini number'
                : undefined}
            />
          </div>
          <div>
            <label htmlFor="phoneNumber2" className="block text-sm font-medium text-gray-700">Secondary Phone Number (Optional)</label>
            <PhoneNumberInput
              value={formData.phoneNumber2}
              onChange={(val) => setFormData({ ...formData, phoneNumber2: val })}
              error={formData.phoneNumber2 && formData.phoneNumber2.length > 0 && !/^[67]\d{7}$/.test(formData.phoneNumber2)
                ? 'Invalid Eswatini number'
                : undefined}
            />
          </div>
        </div>

        <div>
          <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">Date of Birth</label>
          <input
            id="dateOfBirth"
            name="dateOfBirth"
            type="date"
            value={formData.dateOfBirth}
            onChange={handleChange}
            className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700">Address</label>
          <input
            id="address"
            name="address"
            type="text"
            value={formData.address}
            onChange={handleChange}
            className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="gender" className="block text-sm font-medium text-gray-700">Gender</label>
            <select
              id="gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select...</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
          <div>
            <label htmlFor="membershipStatus" className="block text-sm font-medium text-gray-700">Membership Status</label>
            <select
              id="membershipStatus"
              name="membershipStatus"
              value={formData.membershipStatus}
              onChange={handleChange}
              className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md bg-white focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="Full-time Member">Full-time Member</option>
              <option value="Associate Member">Associate Member</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md transition duration-200"
        >
          Register Member
        </button>
      </form>

      {/* Success Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-sm w-full text-center">
            <svg className="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <h3 className="mt-4 text-xl font-semibold text-gray-900">Registration Successful!</h3>
            <p className="mt-2 text-gray-600">{message}</p>
            <div className="mt-6">
              <button
                onClick={closeModal}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Register Another Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberRegistrationForm;

